import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Chart from "react-apexcharts";
import { DataItem } from "../internals/interfaces";
import { Box } from '@mui/material';
import LoadingData from './LoadingData';
import { createCategoryChartConfig, getConsumptionColors } from './shared/ChartConfig';
import PeakConsumption from './PeakConsumption';
import dayjs from 'dayjs';

interface TariffDataItem {
  full_name: string;
  start_date: string;
  end_date: string;
  value_exc_vat: number;
  value_inc_vat: number;
  cost_exc_vat: number;
  cost_inc_vat: number;
  payment_method_id: number;
}

interface ConsumptionChartProps { 
  dateFrom: string;
  dateTo: string;
  consumptionType: number;
  aggregation: string;
  data: DataItem[];
  loading: boolean;
  tariffs?: TariffDataItem[];
  'data-chart-id'?: string;
}

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({ dateFrom, dateTo, consumptionType, aggregation, data, loading, tariffs, 'data-chart-id': dataChartId}) => {
  const [xData, setXData] = React.useState<Date[]>([]);
  const [consumptionData, setConsumptionData] = React.useState<(number | null)[]>([]);
  const [costData, setCostData] = React.useState<(number | null)[]>([]);
  const [priceData, setPriceData] = React.useState<(number | null)[]>([]);
  const [carbonData, setCarbonData] = React.useState<(number | null)[]>([]);
  const [tariffData, setTariffData] = React.useState<number[]>([]);
  const [hasValidPriceData, setHasValidPriceData] = React.useState<boolean>(false);
  const [hasValidCostData, setHasValidCostData] = React.useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to generate complete time sequence based on aggregation
  const generateTimeSequence = (from: string, to: string, agg: string): Date[] => {
    let start = dayjs(from).startOf('day');
    let end = dayjs(to).endOf('day');
    const sequence: Date[] = [];
    
    // Round down start time based on aggregation
    switch (agg) {
      case 'none':
        // Round to nearest 30 minutes
        start = start.minute() < 30 
          ? start.startOf('hour')
          : start.startOf('hour').add(30, 'minute');
        break;
      case 'hour':
        start = start.startOf('hour');
        break;
      case 'day':
        start = start.startOf('day');
        break;
      case 'week':
        // Round to start of week (Monday)
        // dayjs startOf('week') returns Sunday, so we need to adjust to Monday
        const dayOfWeek = start.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        if (dayOfWeek === 0) {
          // If Sunday, go back 6 days to get Monday
          start = start.subtract(6, 'day').startOf('day');
        } else {
          // Otherwise, go back (dayOfWeek - 1) days to get Monday
          start = start.subtract(dayOfWeek - 1, 'day').startOf('day');
        }
        break;
      case 'month':
        start = start.startOf('month');
        break;
      case 'year':
        start = start.startOf('year');
        break;
    }
    
    let current = start;
    
    while (current.isBefore(end) || current.isSame(end)) {
      sequence.push(current.toDate());
      
      // Increment based on aggregation
      switch (agg) {
        case 'none':
          current = current.add(30, 'minute');
          break;
        case 'hour':
          current = current.add(1, 'hour');
          break;
        case 'day':
          current = current.add(1, 'day');
          break;
        case 'week':
          current = current.add(1, 'week');
          break;
        case 'month':
          current = current.add(1, 'month');
          break;
        case 'year':
          current = current.add(1, 'year');
          break;
        default:
          current = current.add(1, 'hour'); // Default to 1 hour
      }
    }
    
    return sequence;
  };

  // Helper function to normalize a date to its aggregation period for comparison
  const normalizeToPeriod = (date: Date, agg: string): string => {
    const d = new Date(date);
    switch (agg) {
      case 'none':
        d.setMinutes(Math.floor(d.getMinutes() / 30) * 30, 0, 0);
        return d.toISOString();
      case 'hour':
        d.setMinutes(0, 0, 0);
        return d.toISOString();
      case 'day':
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      case 'week':
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        d.setDate(diff);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      case 'month':
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      case 'year':
        d.setMonth(0, 1);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      default:
        return d.toISOString();
    }
  };

  React.useEffect(() => {
    if (data.length > 0) {
      // Generate complete time sequence
      const completeTimeSequence = generateTimeSequence(dateFrom, dateTo, aggregation);
      
      // Create a map of data by normalized time period
      const dataMap = new Map<string, DataItem>();
      data.forEach(item => {
        const itemTime = new Date(item.reading_time);
        const normalizedKey = normalizeToPeriod(itemTime, aggregation);
        dataMap.set(normalizedKey, item);
      });
      
      // Build arrays with nulls for missing entries
      const xDataArray: Date[] = [];
      const consumptionArray: (number | null)[] = [];
      const priceArray: (number | null)[] = [];
      const costArray: (number | null)[] = [];
      const carbonArray: (number | null)[] = [];
      
      completeTimeSequence.forEach(timePoint => {
        const normalizedKey = normalizeToPeriod(timePoint, aggregation);
        const matchedItem = dataMap.get(normalizedKey);
        
        xDataArray.push(timePoint);
        
        if (matchedItem) {
          consumptionArray.push(matchedItem.consumption);
          priceArray.push(matchedItem.value_inc_vat ?? null);
          costArray.push(matchedItem.cost_inc_vat ?? null);
          carbonArray.push(matchedItem.gCO_2 ?? null);
        } else {
          // No data for this time point - use null to show gap
          consumptionArray.push(null);
          priceArray.push(null);
          costArray.push(null);
          carbonArray.push(null);
        }
      });
      
      setXData(xDataArray);
      setConsumptionData(consumptionArray);
      
      // Check if price data is valid (not null/undefined)
      const validPriceData = priceArray.filter((val): val is number => val !== null && val !== undefined);
      setPriceData(priceArray);
      setHasValidPriceData(validPriceData.length > 0);
      
      // Check if cost data is valid (not null/undefined)
      const validCostData = costArray.filter((val): val is number => val !== null && val !== undefined);
      setCostData(costArray);
      setHasValidCostData(validCostData.length > 0);
      
      setCarbonData(carbonArray);
      setTariffData((tariffs ?? []).map(item => item.cost_inc_vat).filter((val): val is number => val !== null && val !== undefined));
    } else {
      setXData([]);
      setConsumptionData([]);
      setPriceData([]);
      setCostData([]);
      setCarbonData([]);
      setTariffData([]);
      setHasValidPriceData(false);
      setHasValidCostData(false);
    }
}, [data, tariffs, dateFrom, dateTo, aggregation]);

  const getTotal = (consumption: (number | null)[], factor: number = 1, dp: number = 2, tariff?: number[]) => {
    let sum = 0
    let tariff_sum = 0
    consumption.forEach(num => {
      if (num !== null && num !== undefined) {
        sum += num;
      }
    });
    if (tariff) {
      tariff.forEach(num => {
        tariff_sum += num
      })
    } else {
      tariff_sum = 0
    }
    return ((sum * factor) + tariff_sum).toFixed(dp);
  }

  const formatDayAxis = (value:string) => {
    const date = new Date(value);
    const day = `${date.getDate()}`;
    const month = date.toLocaleString('default', { month: 'short' });
    switch (aggregation) {
      
      case 'minute':
          return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      case 'hour':
          return date.toLocaleTimeString('en-GB', { minute: '2-digit', hour: '2-digit' });
      case 'day':          
          return `${day} ${month}`;
      case 'week':
          return `${day} ${month}`;
      case 'month':
          return date.toLocaleString('default', { month: 'short' });
      case 'year':
          return date.getFullYear().toString();
      default:
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };
  }

  const getSuffix = (day: number) => {
    return (day % 10 === 1 && day !== 11) ? 'st' : 
      (day % 10 === 2 && day !== 12) ? 'nd' : 
      (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  }

  const formatDayTitle = (from:string, to:string) => {
    const dateFrom = new Date(from);
    const dateTo = new Date(to);
    const dayFrom = `${dateFrom.getDate()}`;
    const suffix = getSuffix(dateFrom.getDate())
    const monthFrom = isMobile 
      ? dateFrom.toLocaleString('default', { month: 'short' })
      : dateFrom.toLocaleString('default', { month: 'long' });
    const yearFrom = `${dateFrom.getFullYear()}`;
    const dayTo = `${dateTo.getDate()}`;        
    const suffixTo = getSuffix(dateTo.getDate())
    const monthTo = isMobile 
      ? dateTo.toLocaleString('default', { month: 'short' })
      : dateTo.toLocaleString('default', { month: 'long' });
    const yearTo = `${dateTo.getFullYear()}`;

    switch (aggregation) {
      case 'none':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'hour':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'month':
        if (monthFrom === monthTo) {
          return `${monthFrom} ${yearTo}`;
        } else {          
          if (yearFrom === yearTo) {
            return `${monthFrom} - ${monthTo} ${yearTo}`;
          } else {
            return `${monthFrom} ${yearFrom} - ${monthTo} ${yearTo}`;
          }
        }
      case 'year':
        if (yearFrom === yearTo) {
          return `${yearTo}`;
        } else {          
          return `${yearFrom} - ${yearTo}`;
        }
      default:        
        if (yearFrom === yearTo) {
          if (dayFrom === dayTo) {
            return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
          }
          return `${dayFrom}${suffix} ${monthFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        } else {
          return `${dayFrom}${suffix} ${monthFrom} ${yearFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        }
    }
  }

  const priceLabel = (aggregation === 'none' || aggregation === 'hour') ? "Price (p / kWh)" : "Avg. Price (£ / kWh)"
  const costLabel = (aggregation === 'none' || aggregation === 'hour') ? "Cost (p)" : "Cost (£)"
  
  // Build stroke widths array dynamically
  const strokeWidths = [0]; // Consumption bars always have width 0
  if (hasValidPriceData) strokeWidths.push(2.5);
  if (hasValidCostData) strokeWidths.push(2.5);
  
  // Build y-axis configuration dynamically
  const yAxisConfig = [
    { 
      id: "left",
      title: { text: 'Consumption (kWh)',
        style: {
          fontSize: isMobile ? '13px' : '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: isMobile ? 500 : 700,
          color: theme.palette.text.primary
        }
      },
      decimalsInFloat: 1,
      labels: {
        style: {
          fontSize: isMobile ? '12px' : '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: isMobile ? 400 : 500,
          color: theme.palette.text.primary
        }, 
      },
    }
  ] as any;
  
  // For 'none' or 'hour' aggregation, combine price and cost on single y-axis
  if ((aggregation === 'none' || aggregation === 'hour' || aggregation === 'day') && (hasValidPriceData || hasValidCostData)) {
    yAxisConfig.push({
      id: "right",
      opposite: true, 
      title: { text: "Price & Cost",
        style: {
          fontSize: isMobile ? '13px' : '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: isMobile ? 500 : 700,
          color: theme.palette.text.primary
        } 
      },
      decimalsInFloat: 2,
      labels: {
        style: {
          fontSize: isMobile ? '12px' : '14px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: isMobile ? 400 : 500,
          color: theme.palette.text.primary
        }, 
        formatter: function (value:number) {
          return (aggregation === 'none' || aggregation === 'hour') ? `${value.toFixed(0)}p` : `£${value.toFixed(2)}`;
        }
      },
    });
  } else {
    // For other aggregations, only show cost on separate y-axis (no price)
    if (hasValidCostData) {
      yAxisConfig.push({
        id: "cost",
        opposite: true, 
        title: { text: costLabel,
          style: {
            fontSize: isMobile ? '13px' : '16px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            color: theme.palette.text.primary
          } 
        },
        decimalsInFloat: 2,
        labels: {
          style: {
            fontSize: '16px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            color: theme.palette.text.primary
          }, 
        },
      });
    }
  }
  
  // Theme-aware colors for better dark mode visibility
  const chartColors = getConsumptionColors(theme);
  
  const state = {
    options: {
      ...createCategoryChartConfig({
        theme,
        chartType: 'bar',
        height: 350,
        categories: xData,
        tickAmount: 6,
        tickPlacement: 'between',
        labelFormatter: formatDayAxis,
        chartId: 'consumption',
        isMobile,
        foreColor: chartColors.text,
        showLegend: true
      }),
      // Show a data label on consumption bars when there is no data (null) to indicate "No data".
      dataLabels: {
          enabled: true,
        offsetY: -53,
        formatter: function(val: any, opts: any) {
          // Only show "No data" for the consumption series (seriesIndex 0) when value is null/undefined/NaN
          if (!loading && data.some(x => x.consumption !== null)) {
            if (opts && opts.seriesIndex === 0 && (val === null || val === undefined || Number.isNaN(val))) {
              return 'No data';
            }
          }
          // Do not show labels for other values/series
          return '';
        },
        style: {
          colors: [theme.palette.text.secondary],
          fontSize: isMobile ? '10px' : '14px',
          fontWeight: 600
        },
        background: {
          enabled: false
        }
      },
      stroke: { 
        width: strokeWidths
      },
      yaxis: yAxisConfig,
      tooltip: {
        enabled: true,
        theme: theme.palette.mode,
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        },
        y: {
          formatter: function(value: number | null, { seriesIndex }: { seriesIndex: number }) {
            // Handle null values (gaps in data)
            if (value === null || value === undefined) {
              return 'No data';
            }
            // For price and cost series, show 2 decimal places
            if (seriesIndex > 0) { // seriesIndex 0 is consumption, 1+ are price/cost
              return value.toFixed(2);
            }
            // For consumption, show 1 decimal place
            return value.toFixed(1);
          }
        }
      },
       colors: [chartColors.consumption, ...(hasValidPriceData && (aggregation === 'none' || aggregation === 'hour' || aggregation === 'day') ? [chartColors.price] : []), ...(hasValidCostData ? [chartColors.cost] : [])],
       // Mobile optimizations - only apply on mobile
       ...(isMobile && {
         xaxis: {
           ...createCategoryChartConfig({
             theme,
             chartType: 'bar',
             height: 350,
             categories: xData,
             tickAmount: 6,
             tickPlacement: 'between',
             labelFormatter: formatDayAxis,
             chartId: 'consumption',
             isMobile,
             foreColor: chartColors.text,
             showLegend: true
           }).xaxis,
           tickAmount: 3
         }
       })
     },
    series: [
      {
        name: "Consumption (kWh)",
        type: "bar",
        data: consumptionData,
        yaxis_id: "left"
      },
      ...(hasValidPriceData && (aggregation === 'none' || aggregation === 'hour' || aggregation === 'day') ? [{
        name: priceLabel,
        type: "line",
        data: priceData,
        yaxis_id: "right"
      }] : []),
      ...(hasValidCostData ? [{
        name: costLabel,
        type: "line",
        data: costData,
        yaxis_id: (aggregation === 'none' || aggregation === 'hour' || aggregation === 'day') ? "right" : "cost"
      }] : [])
    ]
  };

  return (
    <Card sx={{ width: '100%', position: 'relative', backgroundColor: theme.palette.background.paper, px: { xs: 1, md: 0 } }} data-chart-id={dataChartId}>
      <CardContent>
        <LoadingData open={loading} />
        <Box sx={{ mb: 1 }}>
          <Typography variant="h2">
            Consumption
          </Typography>
          <Typography variant="h5">
            {formatDayTitle(dateFrom, dateTo)}
          </Typography>
          {data.length > 0 && data.some(item => item.consumption !== null) && (
            <Stack
              direction="row"
              spacing={{ xs: 3, md: 4 }}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 2 }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  {consumptionType === 1 ? 'Electricty' : 'Gas'} consumption
                </Typography>
                <Typography variant="h2">
                  {data.length > 0 ? `${getTotal(consumptionData)} kWh` : 'No data'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Cost
                </Typography>
                <Typography variant="h2">
                  {hasValidCostData ? `£${getTotal(costData, (aggregation === 'none' || aggregation === 'hour') ? 0.01 : 1, 2, tariffData)}` : 'No data'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom>
                  CO₂ emitted
                </Typography>
                <Typography variant="h2">
                  {data.length > 0 ? `${getTotal(carbonData, 1, (aggregation === 'none' || aggregation === 'hour') ? 0 : 2)} ${aggregation === 'none' || aggregation === 'hour' ? ('g') : ('kg')}` : 'No data'}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
        
        <Box sx={{ position: 'relative', ml: { xs: '-20px', md: 0 }, mr: { xs: '-15px', md: 0 }, mb: { xs: '-10px', md: 0 } }}>
          <Chart
            options={state.options}
            series={state.series}
            height={isMobile ? Math.min(250, window.innerWidth * 0.8) : 350}
          />
          
          {/* No data overlay */}
          {!loading && (data.length === 0 || !data.some(x => x.consumption !== null)) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.surface,
                zIndex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                No consumption data
              </Typography>
            </Box>
          )}
        </Box>
        
        {!loading && data.length > 0 && data.some(x => x.consumption !== null) && (
          <PeakConsumption 
                dateFrom={dateFrom} 
                dateTo={dateTo} 
                consumptionType={consumptionType}
              />
        )}
      </CardContent>
    </Card>
  );
};

export default ConsumptionChart;